import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

/**
 * WHY A PLACE WAS RULED OUT — asked at the moment it is.
 *
 * The API refuses a rejection without a reason ("A rejection requires a
 * reason", `CandidatesService`), and the Locations tab and Explore both sent
 * Reject without one, so the button had never once worked (found 2026-10-30).
 * The reason is stored on the candidate (`rejection_reason`) as the record of
 * the decision, and Restore puts the place back. Nothing shows it yet: no read
 * returns the stored reason, so a teammate opening a rejected place cannot see
 * why it was set aside. Showing it needs that read first.
 *
 * Four answers cover what a location manager rules a place out for; "Other"
 * takes a sentence. One click on an answer rejects — no second confirm, because
 * Restore undoes it.
 */
export const REJECT_REASONS: readonly string[] = Object.freeze([
  'Wrong look for the scene',
  'Wrong area or too far',
  'Over budget',
  'Not available on our dates',
]);

export function RejectWithReason({
  testId,
  disabled,
  title,
  className = 'ctl',
  placement = 'down',
  onReject,
}: {
  /** The button's test id; the prompt's parts take it as a prefix. */
  testId: string;
  disabled: boolean;
  title?: string;
  className?: string;
  /** `up` where the button sits at the bottom of a panel. */
  placement?: 'down' | 'up';
  onReject: (reason: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [other, setOther] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const promptId = useId();

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    root.current?.querySelector<HTMLElement>('[data-reason]')?.focus();
    return () => {
      document.removeEventListener('mousedown', close);
    };
  }, [open]);

  const choose = (reason: string) => {
    const text = reason.trim();
    if (text === '') return;
    setOpen(false);
    setOther('');
    onReject(text);
  };

  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
    button.current?.focus();
  };

  return (
    <div className="rj" ref={root} onKeyDown={onKey}>
      <button
        ref={button}
        type="button"
        className={className}
        data-testid={testId}
        disabled={disabled}
        title={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? promptId : undefined}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        Reject
      </button>
      {open ? (
        <div className="rj-prompt" data-placement={placement} role="dialog" id={promptId} aria-label="Why reject this place?" data-testid={`${testId}-prompt`}>
          <p className="rj-title">Why reject it?</p>
          <div className="rj-reasons">
            {REJECT_REASONS.map((reason, index) => (
              <button
                key={reason}
                type="button"
                className="rj-reason"
                data-reason
                data-testid={`${testId}-reason-${String(index)}`}
                onClick={() => {
                  choose(reason);
                }}
              >
                {reason}
              </button>
            ))}
          </div>
          <form
            className="rj-other"
            onSubmit={(event) => {
              event.preventDefault();
              choose(other);
            }}
          >
            <input
              type="text"
              value={other}
              maxLength={500}
              placeholder="Another reason"
              aria-label="Another reason"
              data-testid={`${testId}-other`}
              onChange={(event) => {
                setOther(event.target.value);
              }}
            />
            <button type="submit" className="ctl" disabled={other.trim() === ''} data-testid={`${testId}-other-submit`}>
              Reject
            </button>
          </form>
          <p className="rj-note">You can restore it later.</p>
        </div>
      ) : null}
    </div>
  );
}
